/**
 * The Channel Partner profile fields, shared by the application form and the
 * Hub profile editor. Must render inside a react-hook-form <Form> whose
 * values include `ProfileFormInput`.
 *
 * Partner types and states come from `/options`: submit the type's `id`,
 * show its `label`, and send the state exactly as returned. City is free text.
 */
import type * as React from 'react';
import { Building2, Mail, MapPin } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { derivePanType, panTypeLabel } from '@finmin/utils';
import {
  FancySelect,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@finmin/ui';
import type { ChannelPartnerOptions } from '@/api/channel-partners';
import { OTHERS_PARTNER_TYPE, type ProfileFormInput } from '../application-schema';
import { DocumentAttachmentField } from './document-attachment-field';

function Required() {
  return (
    <span className="text-destructive" aria-label="required">
      {' '}*
    </span>
  );
}

function Optional() {
  return <span className="font-normal text-muted-foreground"> (optional)</span>;
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="space-y-4">
      <legend className="mb-1">
        <span className="block font-display text-lg font-semibold tracking-tight text-foreground">{title}</span>
        {description && <span className="block text-xs text-muted-foreground">{description}</span>}
      </legend>
      {children}
    </fieldset>
  );
}

export function ChannelPartnerFieldset({
  options,
  disabled,
}: {
  options: ChannelPartnerOptions;
  disabled?: boolean;
}) {
  const form = useFormContext<ProfileFormInput>();
  const partnerType = form.watch('partner_type');
  const pan = form.watch('pan');
  const panType = pan ? derivePanType(pan) : null;

  return (
    <div className="space-y-8">
      <Section title="Partner details">
        <FormField
          control={form.control}
          name="cp_email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Channel Partner email
                <Required />
              </FormLabel>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="partner@example.com"
                    disabled={disabled}
                    className="pl-9"
                  />
                </FormControl>
              </div>
              <FormDescription>
                Channel Partner updates go here. It can differ from your account email and does not
                change it.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="partner_type"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>
                  Partner type
                  <Required />
                </FormLabel>
                <FormControl>
                  <FancySelect
                    options={options.partner_types.map((t) => ({ value: t.id, label: t.label }))}
                    value={field.value}
                    onChange={field.onChange}
                    invalid={Boolean(fieldState.error)}
                    disabled={disabled}
                    placeholder="Select a partner type"
                    ariaLabel="Partner type"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organisation_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Organisation / business name
                  <Optional />
                </FormLabel>
                <div className="relative">
                  <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <FormControl>
                    <Input {...field} autoComplete="organization" disabled={disabled} className="pl-9" />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {partnerType === OTHERS_PARTNER_TYPE && (
          <FormField
            control={form.control}
            name="other_partner_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Describe your partner type
                  <Required />
                </FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. Insurance advisor" disabled={disabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="state"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>
                  State
                  <Required />
                </FormLabel>
                <FormControl>
                  <FancySelect
                    options={options.states.map((s) => ({ value: s, label: s }))}
                    value={field.value}
                    onChange={field.onChange}
                    invalid={Boolean(fieldState.error)}
                    disabled={disabled}
                    searchable
                    leadingIcon={MapPin}
                    placeholder="Select your state"
                    ariaLabel="State"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  City
                  <Required />
                </FormLabel>
                <FormControl>
                  <Input {...field} autoComplete="address-level2" placeholder="e.g. New Delhi" disabled={disabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Section>

      <Section title="About you">
        <FormField
          control={form.control}
          name="acquisition_source"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                How did you hear about FinminOS / Referred by
                <Optional />
              </FormLabel>
              <FormControl>
                <Input {...field} disabled={disabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="goals"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                How do you want to become successful as a Channel Partner?
                <Optional />
              </FormLabel>
              <FormControl>
                <Textarea {...field} rows={3} disabled={disabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Section>

      <Section
        title="Tax details"
        description="All optional — PAN, GSTIN and their documents are never needed to activate your account."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="pan"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  PAN
                  <Optional />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase().slice(0, 10))}
                    placeholder="ABCDE1234F"
                    autoCapitalize="characters"
                    disabled={disabled}
                    className="font-mono uppercase tracking-wider"
                  />
                </FormControl>
                {panType ? (
                  <FormDescription>{panTypeLabel(panType)} PAN. An organisation uses its organisation PAN.</FormDescription>
                ) : (
                  <FormDescription>An organisation uses its organisation PAN.</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pan_document_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  PAN card
                  <Optional />
                </FormLabel>
                <DocumentAttachmentField
                  documentType="pan"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={disabled}
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gstin"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  GSTIN
                  <Optional />
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase().slice(0, 15))}
                    placeholder="22ABCDE1234F1Z5"
                    autoCapitalize="characters"
                    disabled={disabled}
                    className="font-mono uppercase tracking-wider"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gst_document_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  GST certificate
                  <Optional />
                </FormLabel>
                <DocumentAttachmentField
                  documentType="gst"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={disabled}
                />
              </FormItem>
            )}
          />
        </div>
      </Section>
    </div>
  );
}
